'use client';

import React, { useState, useMemo } from 'react';
import { 
  TbElevator, TbCoffee, TbWashMachine, 
  TbShoppingCart, TbFirstAidKit, TbTreadmill, TbMosque, 
  TbPrinter, TbCut, TbTriangle, TbCar, 
  TbCircleCheck, TbToolsKitchen2 // آیکون‌های جدید اضافه شده طبق تصویر
} from 'react-icons/tb';
import { FiChevronLeft, FiChevronUp } from 'react-icons/fi';

interface HotelFeaturesProps {
  facilities?: string[];
  specialAmenities?: string[];
}

// نگاشت هوشمند نام‌ها به آیکون‌های دقیق (دقیقا شبیه تصویر جدید)
const getIconForFacility = (name: string) => {
  if (!name) return <TbCircleCheck />;
  
  // آیکون قاشق و چنگال برای رستوران (طبق تصویر شما)
  if (name.includes('رستوران') || name.includes('غذا')) return <TbToolsKitchen2 />;
  
  // سایر آیکون‌های خاص قبلی
  if (name.includes('آسانسور')) return <TbElevator />;
  if (name.includes('تاکسی')) return <TbCar />;
  if (name.includes('کافی شاپ') || name.includes('کافه')) return <TbCoffee />;
  if (name.includes('خشکشویی') || name.includes('لاندری')) return <TbWashMachine />;
  if (name.includes('فروشگاه')) return <TbShoppingCart />;
  if (name.includes('کمک های اولیه') || name.includes('پزشک')) return <TbFirstAidKit />;
  if (name.includes('باشگاه') || name.includes('ورزشی')) return <TbTreadmill />;
  if (name.includes('نمازخانه')) return <TbMosque />;
  if (name.includes('فتوکپی') || name.includes('پرینت')) return <TbPrinter />;
  if (name.includes('آرایشگاه') || name.includes('زیبایی')) return <TbCut />;
  if (name.includes('بیلیارد')) return <TbTriangle />;
  
  // آیکون پیش‌فرض برای تمام موارد دیگر (پارکینگ، گاز، اینترنت، سرویس بهداشتی و...)
  // دقیقاً همان تیک دایره‌ای ظریف داخل عکس
  return <TbCircleCheck />; 
};

const HotelFeatures: React.FC<HotelFeaturesProps> = ({ facilities = [], specialAmenities = [] }) => {
  const [isExpanded, setIsExpanded] = useState(false);

  // ادغام تمامی امکانات دریافت شده از API
  const allFeatures = useMemo(() => {
    const combined = [...facilities, ...specialAmenities];
    return Array.from(new Set(combined)).filter(Boolean); // حذف موارد تکراری و خالی
  }, [facilities, specialAmenities]);

  if (allFeatures.length === 0) return null;

  // نمایش ۱۲ آیتم در حالت پیش‌فرض (مثل دیزاین)
  const INITIAL_LIMIT = 12;
  const displayedFeatures = isExpanded ? allFeatures : allFeatures.slice(0, INITIAL_LIMIT);
  const hasMore = allFeatures.length > INITIAL_LIMIT;

  return (
    <div className="features-wrapper" dir="rtl">
      {/* عنوان بخش */}
      <h3 className="features-main-title">امکانات و ویژگی ها</h3>

      <div className="features-box">
        <div className="features-grid">
          {displayedFeatures.map((feature, index) => {
            const isHighlight = feature.includes('خشکشویی');

            return (
              <div key={index} className="feature-item">
                <span className="feature-icon">
                  {getIconForFacility(feature)}
                </span>
                <span className={`feature-text ${isHighlight ? 'highlight-text' : ''}`}>
                  {feature}
                </span>
              </div>
            );
          })}
        </div>

        {hasMore && (
          <div className="expand-btn-container">
            <button 
              className="expand-btn" 
              onClick={() => setIsExpanded(!isExpanded)}
            >
              {isExpanded ? 'مشاهده امکانات کمتر' : 'مشاهده امکانات بیشتر'}
              {isExpanded ? <FiChevronUp className="btn-icon" /> : <FiChevronLeft className="btn-icon" />}
            </button>
          </div>
        )}
      </div>

      <style jsx>{`
          .features-wrapper {
          width: 100%;
          /* margin: 2rem 0; ----> این خط را حتما پاک کنید */
          font-family: inherit;
        }
        .features-main-title {
          font-size: 1.1rem;
          font-weight: 800;
          color: #2c2c2c;
          margin-bottom: 1.2rem;
          text-align: right;
        }

        .features-box {
          border: 1px solid #e0e4e7;
          border-radius: 12px;
          padding: 1.8rem 2rem 1.2rem 2rem;
          background-color: #ffffff;
        }

        .features-grid {
          display: grid;
          grid-template-columns: repeat(4, 1fr);
          row-gap: 1.8rem;
          column-gap: 1rem;
        }

        .feature-item {
          display: flex;
          align-items: center;
          justify-content: flex-start;
          gap: 0.5rem;
        }

        .feature-icon {
          display: flex;
          align-items: center;
          justify-content: center;
          color: #5a6650; /* رنگ زیتونی/طوسی دقیقاً شبیه آیکون‌های عکس */
          font-size: 1.3rem;
        }

        .feature-icon svg {
          stroke-width: 1.5; /* ضخامت ظریف آیکون‌ها مشابه عکس */
        }

        .feature-text {
          font-size: 0.85rem;
          color: #3b3b3b;
          font-weight: 600;
          white-space: nowrap; /* جلوگیری از شکستن خطوط کوتاه */
          overflow: hidden;
          text-overflow: ellipsis;
        }

        .highlight-text {
          color: #d35400; 
        }

        .expand-btn-container {
          display: flex;
          justify-content: flex-end;
          margin-top: 2rem;
        }

        .expand-btn {
          background: none;
          border: none;
          color: #2b789d;
          font-size: 0.8rem;
          font-weight: 700;
          cursor: pointer;
          display: flex;
          align-items: center;
          gap: 0.2rem;
          padding: 0;
          transition: opacity 0.2s ease;
        }

        .expand-btn:hover {
          opacity: 0.8;
        }

        .btn-icon {
          font-size: 1rem;
          margin-top: 2px;
        }

        @media (max-width: 1024px) {
          .features-grid {
            grid-template-columns: repeat(3, 1fr);
          }
        }

        @media (max-width: 768px) {
          .features-grid {
            grid-template-columns: repeat(2, 1fr);
          }
          .features-box {
            padding: 1.5rem;
          }
          .feature-text {
            white-space: normal; /* در موبایل متن‌ها بتوانند بشکنند */
          }
        }

        @media (max-width: 480px) {
          .features-grid {
            grid-template-columns: 1fr;
          }
          .features-box {
            padding: 1.2rem;
          }
          .expand-btn-container {
            justify-content: flex-start;
          }
        }
      `}</style>
    </div>
  );
};

export default HotelFeatures;
